// eslint-disable-next-line no-restricted-imports
import { gql, useMutation } from "@apollo/client";
import { Typography } from "@material-ui/core";
import { useMergeLink } from "@mergeapi/react-merge-link";
import IntegrationCard from "components/legacy/IntegrationCard";
import {
  DeleteMergeAccount,
  GenerateMergeLinkTokenForCurrentUser,
  IntegrationEnum,
  StoreMergeAccountToken,
} from "generated/schemaTypes";
import greenhouseIconLg from "icons/greenhouse-icon-lg.svg";
import leverIcon from "icons/lever-logo-standard.svg";
import { useCallback } from "react";

type MergeIntegrationCardProps = {
  type: IntegrationEnum;
  connected: boolean;
  mergeAccountId: string | null;
  afterMergeConnect: () => void;
};

const atsMap: Partial<
  Record<IntegrationEnum, { logo: { src: string }; label: string }>
> = {
  [IntegrationEnum.GREENHOUSE_MERGE]: {
    logo: greenhouseIconLg,
    label: "Greenhouse",
  },
  [IntegrationEnum.LEVER]: {
    logo: leverIcon,
    label: "Lever",
  },
};

const GENERATE_MERGE_LINK_TOKEN = gql`
  mutation GenerateMergeLinkTokenForCurrentUser(
    $input: GenerateMergeLinkTokenInputPrismaProxy!
  ) {
    generateMergeLinkTokenForCurrentUser(input: $input) {
      accountId
      linkToken
    }
  }
`;

const STORE_MERGE_ACCOUNT_TOKEN = gql`
  mutation StoreMergeAccountToken(
    $input: StoreMergeAccountTokenInputPrismaProxy!
  ) {
    storeMergeAccountToken(input: $input) {
      success
      message
    }
  }
`;

const DELETE_MERGE_ACCOUNT = gql`
  mutation DeleteMergeAccount($input: DeleteMergeAccountInputPrismaProxy!) {
    deleteMergeAccount(input: $input) {
      success
      message
    }
  }
`;

function MergeIntegrationCard({
  type,
  connected,
  afterMergeConnect,
  mergeAccountId,
}: MergeIntegrationCardProps) {
  const [connect, { data }] = useMutation<GenerateMergeLinkTokenForCurrentUser>(
    GENERATE_MERGE_LINK_TOKEN
  );

  const [onMergeSuccess] = useMutation<StoreMergeAccountToken>(
    STORE_MERGE_ACCOUNT_TOKEN
  );

  const [deleteAccount] = useMutation<DeleteMergeAccount>(DELETE_MERGE_ACCOUNT);

  const accountId = data?.generateMergeLinkTokenForCurrentUser?.accountId;

  const onDisconnect = async () => {
    await deleteAccount({
      variables: {
        input: {
          accountId: mergeAccountId,
        },
      },
    });
    afterMergeConnect();
  };

  const onSuccess = useCallback(
    async (publicToken) => {
      if (accountId && publicToken) {
        await onMergeSuccess({
          variables: {
            input: {
              accountId,
              publicToken,
            },
          },
        });
        afterMergeConnect();
      }
    },
    [accountId, onMergeSuccess, afterMergeConnect]
  );

  const { open } = useMergeLink({
    linkToken: data?.generateMergeLinkTokenForCurrentUser?.linkToken ?? "",
    onSuccess,
  });

  const onConnect = async () => {
    await connect({
      variables: {
        input: {
          integration: atsMap[type]?.label.toLowerCase(),
        },
      },
    });
    open();
  };

  const icon = atsMap[type]?.logo.src ?? "";
  const label = atsMap[type]?.label ?? "";

  return (
    <IntegrationCard
      icon={icon}
      label={label}
      connected={connected}
      onConnect={onConnect}
      onDisconnect={onDisconnect}
      disconnectSaving={false}
    >
      {connected ? (
        <Typography>Connected to {label} account.</Typography>
      ) : (
        <>
          <Typography>Connect to your {label} account.</Typography>
          <br />
          <Typography>
            For help enabling the integration, send us a chat below.
          </Typography>
        </>
      )}
    </IntegrationCard>
  );
}

export default MergeIntegrationCard;
